import axios from 'axios';
import * as C from '../constants/reportingConstants';
import { normalizeReportTemplates, applyFunctionTemplate, DATA_SOURCE_TARGET_TENANT } from '../components/reporting/engine/functionSqlTemplates';
import { runWithConcurrencyPool } from '../components/reporting/engine/reportingChartCache';
import { normalizeCategoryLabelMap } from '../components/reporting/engine/reportingCategoryLabelMaps';
import {
    DEFAULT_DASHBOARD_SECTIONS,
    buildDefaultDashboards,
} from '../components/reporting/engine/defaultDashboardConfig';
import {
    pickDefaultDataSourceId,
} from '../components/reporting/engine/reportingConnectionUtils';

const BASE_DS  = '/dotnet_api/v1/ReportDataSource';
const BASE_FN  = '/dotnet_api/v1/ReportFunction';
const BASE_DB  = '/dotnet_api/v1/ReportDashboard';
const BASE_MAP = '/dotnet_api/v1/ReportCategoryLabelMap';

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const token = (getState) => getState().userLogin.userInfo.token;

/** @param {import('axios').AxiosError} e */
function reportActionError(e) {
    const body = e.response?.data;
    return { error: body?.detail ?? e.message, policy: body?.policy };
}

// ─── Data Sources ────────────────────────────────────────────────────────────

export const detectDataSource = () => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_DETECT_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_DS}/detect`, auth(token(getState)));
        dispatch({ type: C.REPORT_DATASOURCE_DETECT_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_DATASOURCE_DETECT_FAIL, payload: e.response?.data?.detail ?? e.message });
        return null;
    }
};

export const ensureDefaultDataSources = () => async (dispatch, getState) => {
    try {
        const { data } = await axios.post(`${BASE_DS}/ensure-defaults`, {}, auth(token(getState)));
        await dispatch(listDataSources());
        return data;
    } catch (e) {
        return { error: e.response?.data?.detail ?? e.message };
    }
};

// ─── Category label maps ─────────────────────────────────────────────────────

async function loadCategoryLabelMaps(dispatch, getState) {
    dispatch({ type: C.REPORT_CATEGORY_LABEL_MAP_LIST_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_MAP}/all?limit=200`, auth(token(getState)));
        const raw = data?.items ?? data?.Items ?? data ?? [];
        const items = (Array.isArray(raw) ? raw : []).map(normalizeCategoryLabelMap);
        dispatch({ type: C.REPORT_CATEGORY_LABEL_MAP_LIST_SUCCESS, payload: items });
        return items;
    } catch (e) {
        dispatch({ type: C.REPORT_CATEGORY_LABEL_MAP_LIST_FAIL, payload: e.response?.data?.detail ?? e.message });
        return [];
    }
}

export const listCategoryLabelMaps = () => (dispatch, getState) =>
    loadCategoryLabelMaps(dispatch, getState);

export const ensureDefaultCategoryLabelMaps = () => async (dispatch, getState) => {
    try {
        const { data } = await axios.post(`${BASE_MAP}/ensure-defaults`, {}, auth(token(getState)));
        await loadCategoryLabelMaps(dispatch, getState);
        return data;
    } catch (e) {
        return { error: e.response?.data?.detail ?? e.message };
    }
};

export const createCategoryLabelMap = (dto) => async (dispatch, getState) => {
    try {
        const { data } = await axios.post(BASE_MAP, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        await loadCategoryLabelMaps(dispatch, getState);
        return data;
    } catch {
        return null;
    }
};

export const patchCategoryLabelMap = (id, dto) => async (dispatch, getState) => {
    try {
        const { data } = await axios.patch(`${BASE_MAP}/${id}`, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        await loadCategoryLabelMaps(dispatch, getState);
        return data;
    } catch {
        return null;
    }
};

export const deleteCategoryLabelMap = (id) => async (dispatch, getState) => {
    try {
        await axios.delete(`${BASE_MAP}/${id}`, auth(token(getState)));
        await loadCategoryLabelMaps(dispatch, getState);
        return true;
    } catch {
        return false;
    }
};

export const listDataSourceSchemas = (id) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_SCHEMAS_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_DS}/${id}/schemas`, auth(token(getState)));
        dispatch({ type: C.REPORT_DATASOURCE_SCHEMAS_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_DATASOURCE_SCHEMAS_FAIL, payload: e.response?.data?.detail ?? e.message });
        return [];
    }
};

export const createDataSource = (dto) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_CREATE_REQUEST });
    try {
        const { data } = await axios.post(BASE_DS, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_DATASOURCE_CREATE_SUCCESS, payload: data });
        return data;
    } catch (e) {
        const err = reportActionError(e);
        dispatch({ type: C.REPORT_DATASOURCE_CREATE_FAIL, payload: err.error });
        return err;
    }
};

export const updateDataSource = (id, dto) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_UPDATE_REQUEST });
    try {
        const { data } = await axios.put(`${BASE_DS}/${id}`, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_DATASOURCE_UPDATE_SUCCESS, payload: data });
        await dispatch(listDataSources());
        return data;
    } catch (e) {
        const err = reportActionError(e);
        dispatch({ type: C.REPORT_DATASOURCE_UPDATE_FAIL, payload: err.error });
        return err;
    }
};

export const deleteDataSource = (id) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_DELETE_REQUEST });
    try {
        await axios.delete(`${BASE_DS}/${id}`, auth(token(getState)));
        dispatch({ type: C.REPORT_DATASOURCE_DELETE_SUCCESS, payload: id });
        return true;
    } catch (e) {
        const err = reportActionError(e);
        dispatch({ type: C.REPORT_DATASOURCE_DELETE_FAIL, payload: err.error });
        return err;
    }
};

export const executeRawQuery = (dataSourceId, sql, rowLimit = 500) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_QUERY_REQUEST });
    try {
        const { data } = await axios.post(
            `${BASE_DS}/${dataSourceId}/query`,
            { sql, rowLimit },
            { ...auth(token(getState)), headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' } }
        );
        dispatch({ type: C.REPORT_DATASOURCE_QUERY_SUCCESS, payload: data });
        return data;
    } catch (e) {
        const msg = e.response?.data?.detail ?? e.message;
        dispatch({ type: C.REPORT_DATASOURCE_QUERY_FAIL, payload: msg });
        return null;
    }
};

export const resetRawQuery = () => ({ type: C.REPORT_DATASOURCE_QUERY_RESET });

export const listPgFunctions = (dataSourceId, schema = 'public') => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_PG_FUNCTIONS_REQUEST });
    try {
        const params = new URLSearchParams({ dataSourceId, schema });
        const { data } = await axios.get(`${BASE_FN}/pg-functions?${params}`, auth(token(getState)));
        dispatch({ type: C.REPORT_PG_FUNCTIONS_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_PG_FUNCTIONS_FAIL, payload: e.response?.data?.detail ?? e.message });
        return [];
    }
};

export const createReportFunction = (dto) => async (dispatch, getState) => {
    try {
        const { data } = await axios.post(BASE_FN, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        await dispatch(listReportFunctions());
        return data;
    } catch (e) {
        return reportActionError(e);
    }
};

export const updateReportFunction = (id, dto) => async (dispatch, getState) => {
    try {
        const { data } = await axios.put(`${BASE_FN}/${id}`, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        await dispatch(listReportFunctions());
        return data;
    } catch (e) {
        return reportActionError(e);
    }
};

export const deleteReportFunction = (id) => async (dispatch, getState) => {
    try {
        await axios.delete(`${BASE_FN}/${id}`, auth(token(getState)));
        await dispatch(listReportFunctions());
        return true;
    } catch (e) {
        return reportActionError(e);
    }
};

export const listDataSources = () => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DATASOURCE_LIST_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_DS}/all?limit=100`, auth(token(getState)));
        dispatch({ type: C.REPORT_DATASOURCE_LIST_SUCCESS, payload: data?.items ?? data });
    } catch (e) {
        dispatch({ type: C.REPORT_DATASOURCE_LIST_FAIL, payload: e.response?.data?.detail ?? e.message });
    }
};

export const getDataSourceById = (id) => async (dispatch, getState) => {
    try {
        const { data } = await axios.get(`${BASE_DS}/${id}`, auth(token(getState)));
        return data;
    } catch (e) {
        return reportActionError(e);
    }
};

// ─── Functions ───────────────────────────────────────────────────────────────

export const getReportFunctionById = (id) => async (dispatch, getState) => {
    try {
        const { data } = await axios.get(`${BASE_FN}/${id}`, auth(token(getState)));
        return data;
    } catch (e) {
        return reportActionError(e);
    }
};

export const listReportFunctions = () => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_FUNCTION_LIST_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_FN}/all?limit=200`, auth(token(getState)));
        dispatch({ type: C.REPORT_FUNCTION_LIST_SUCCESS, payload: data?.items ?? data });
    } catch (e) {
        dispatch({ type: C.REPORT_FUNCTION_LIST_FAIL, payload: e.response?.data?.detail ?? e.message });
    }
};

export const listReportFunctionTemplates = () => async (dispatch, getState) => {
    const { templates, loading } = getState().reportFunctionTemplates ?? {};
    if (templates?.length > 0 || loading) return templates ?? [];
    dispatch({ type: C.REPORT_FUNCTION_TEMPLATES_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_FN}/templates`, auth(token(getState)));
        const payload = {
            templates: normalizeReportTemplates(data?.templates ?? data?.Templates),
            categories: data?.categories ?? data?.Categories ?? {},
        };
        dispatch({ type: C.REPORT_FUNCTION_TEMPLATES_SUCCESS, payload });
        return payload.templates;
    } catch (e) {
        dispatch({ type: C.REPORT_FUNCTION_TEMPLATES_FAIL, payload: e.response?.data?.detail ?? e.message });
        return [];
    }
};

export const deployReportFunction = (dataSourceId, sql) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_FUNCTION_DEPLOY_REQUEST });
    try {
        const { data } = await axios.post(`${BASE_FN}/deploy`, { dataSourceId, sql }, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_FUNCTION_DEPLOY_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_FUNCTION_DEPLOY_FAIL, payload: e.response?.data?.detail ?? e.message });
        return null;
    }
};

export const describeReportFunction = (dataSourceId, schema, functionName) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_FUNCTION_DESCRIBE_REQUEST });
    try {
        const params = new URLSearchParams({ dataSourceId, schema: schema ?? 'public', functionName });
        const { data } = await axios.get(`${BASE_FN}/describe?${params}`, auth(token(getState)));
        dispatch({ type: C.REPORT_FUNCTION_DESCRIBE_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_FUNCTION_DESCRIBE_FAIL, payload: e.response?.data?.detail ?? e.message });
        return null;
    }
};

export const getReportFunctionDefinition = (dataSourceId, schema, functionName) => async (dispatch, getState) => {
    try {
        const params = new URLSearchParams({
            dataSourceId,
            schema: schema ?? 'public',
            functionName,
        });
        const { data } = await axios.get(`${BASE_FN}/function-definition?${params}`, auth(token(getState)));
        return data;
    } catch (e) {
        return { exists: false, ...reportActionError(e) };
    }
};

export const resetDescribeReportFunction = () => ({ type: C.REPORT_FUNCTION_DESCRIBE_RESET });

export const executeReportFunction = (functionId, filter = {}) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_FUNCTION_EXECUTE_REQUEST, meta: { functionId } });
    try {
        const { data } = await axios.post(`${BASE_FN}/${functionId}/execute`, filter, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_FUNCTION_EXECUTE_SUCCESS, meta: { functionId }, payload: data });
        return data;
    } catch (e) {
        const msg = e.response?.data?.detail ?? e.message;
        dispatch({ type: C.REPORT_FUNCTION_EXECUTE_FAIL, meta: { functionId }, payload: msg });
        return { error: msg };
    }
};

// ─── Dashboards ──────────────────────────────────────────────────────────────

export const listReportDashboardsByUser = (userId) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DASHBOARD_LIST_REQUEST });
    try {
        const { data } = await axios.get(`${BASE_DB}/user/${userId}`, auth(token(getState)));
        dispatch({ type: C.REPORT_DASHBOARD_LIST_SUCCESS, payload: Array.isArray(data) ? data : (data?.items ?? []) });
    } catch (e) {
        dispatch({ type: C.REPORT_DASHBOARD_LIST_FAIL, payload: e.response?.data?.detail ?? e.message });
    }
};

export const createReportDashboard = (dto) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DASHBOARD_CREATE_REQUEST });
    try {
        const { data } = await axios.post(BASE_DB, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_DASHBOARD_CREATE_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_DASHBOARD_CREATE_FAIL, payload: e.response?.data?.detail ?? e.message });
        return null;
    }
};

export const updateReportDashboard = (id, dto) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DASHBOARD_UPDATE_REQUEST });
    try {
        const { data } = await axios.put(`${BASE_DB}/${id}`, dto, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_DASHBOARD_UPDATE_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_DASHBOARD_UPDATE_FAIL, payload: e.response?.data?.detail ?? e.message });
        return null;
    }
};

export const patchReportDashboard = (id, patch) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DASHBOARD_UPDATE_REQUEST });
    try {
        const { data } = await axios.patch(`${BASE_DB}/${id}`, patch, {
            ...auth(token(getState)),
            headers: { ...auth(token(getState)).headers, 'Content-Type': 'application/json' },
        });
        dispatch({ type: C.REPORT_DASHBOARD_UPDATE_SUCCESS, payload: data });
        return data;
    } catch (e) {
        dispatch({ type: C.REPORT_DASHBOARD_UPDATE_FAIL, payload: e.response?.data?.detail ?? e.message });
        return null;
    }
};

export const deleteReportDashboard = (id) => async (dispatch, getState) => {
    dispatch({ type: C.REPORT_DASHBOARD_DELETE_REQUEST });
    try {
        await axios.delete(`${BASE_DB}/${id}`, auth(token(getState)));
        dispatch({ type: C.REPORT_DASHBOARD_DELETE_SUCCESS, payload: id });
        return true;
    } catch (e) {
        const err = reportActionError(e);
        dispatch({ type: C.REPORT_DASHBOARD_DELETE_FAIL, payload: err.error });
        return err;
    }
};

const LOAD_DEFAULTS_CONCURRENCY = 5;

function dataSourceIdForTemplate(tpl, tenantDataSourceId) {
    return tenantDataSourceId;
}

async function ensureReportingDataSources(dispatch, getState) {
    await dispatch(ensureDefaultDataSources());
    await dispatch(ensureDefaultCategoryLabelMaps());
    await dispatch(listDataSources());
    const dataSources = getState().reportDataSourceList?.items ?? [];

    const tenantDataSourceId = pickDefaultDataSourceId(dataSources);
    if (!tenantDataSourceId) {
        return { error: 'No tenant data source available. Check ConnectionStrings and restart the API.' };
    }

    return {
        tenantDataSourceId,
        dataSourceIds: { tenant: tenantDataSourceId },
    };
}

/**
 * Delete all organisation report dashboards and registered report functions.
 * Does not remove data sources or PostgreSQL functions on the database.
 */
export const wipeReportingWorkspace = (applicationUserId) => async (dispatch, getState) => {
    await dispatch(listReportDashboardsByUser(applicationUserId));
    const dashboards = getState().reportDashboardList?.items ?? [];
    for (const db of dashboards) {
        const id = db.id ?? db.Id;
        if (id) await dispatch(deleteReportDashboard(id));
    }

    await dispatch(listReportFunctions());
    const fns = getState().reportFunctionList?.items ?? [];
    for (const fn of fns) {
        const id = fn.id ?? fn.Id;
        if (id) await dispatch(deleteReportFunction(id));
    }

    await dispatch(listReportFunctions());
    await dispatch(listReportDashboardsByUser(applicationUserId));
    return {
        dashboardsRemoved: dashboards.length,
        functionsRemoved: fns.length,
    };
};

/**
 * Deploy all SQL Lab templates, register functions, and create the default reporting dashboard.
 * @param {string} applicationUserId
 * @param {{ wipe?: boolean, onProgress?: (p: { step: string, current?: number, total?: number, message?: string }) => void }} [options]
 */
export const loadDefaultDashboard = (applicationUserId, options = {}) => async (dispatch, getState) => {
    const { onProgress, wipe = false } = options;
    const report = (step, extra = {}) => {
        if (onProgress) onProgress({ step, ...extra });
    };

    try {
        if (wipe) {
            report('wipe', { message: 'Removing existing dashboards and functions…' });
            await dispatch(wipeReportingWorkspace(applicationUserId));
        }

        report('datasource', { message: 'Resolving data sources…' });
        const resolved = await ensureReportingDataSources(dispatch, getState);
        if (resolved.error) return { error: resolved.error };

        const { tenantDataSourceId, dataSourceIds } = resolved;

        report('templates', { message: 'Loading SQL templates…' });
        const { data: templatePayload } = await axios.get(`${BASE_FN}/templates`, auth(token(getState)));
        const allTemplates = normalizeReportTemplates(templatePayload?.templates ?? templatePayload?.Templates ?? []);
        const categories = templatePayload?.categories ?? templatePayload?.Categories ?? {};
        dispatch({
            type: C.REPORT_FUNCTION_TEMPLATES_SUCCESS,
            payload: { templates: allTemplates, categories },
        });

        const templatesToDeploy = allTemplates;

        const total = templatesToDeploy.length;
        let deployed = 0;
        const deployErrors = [];

        const deployOne = async (tpl) => {
            const fnName = tpl.defaultFunctionName ?? tpl.key;
            const dsId = dataSourceIdForTemplate(tpl, tenantDataSourceId);
            const sql = applyFunctionTemplate(tpl.key, 'public', fnName, allTemplates);
            if (!sql?.trim()) {
                deployErrors.push(`${fnName}: empty template`);
                return;
            }
            const result = await dispatch(deployReportFunction(dsId, sql));
            if (!result) {
                deployErrors.push(fnName);
            }
            deployed += 1;
            report('deploy', { current: deployed, total, message: `Deploying ${fnName}…` });
        };

        report('deploy', { current: 0, total, message: 'Deploying functions…' });
        await runWithConcurrencyPool(
            templatesToDeploy.map(tpl => () => deployOne(tpl)),
            LOAD_DEFAULTS_CONCURRENCY,
        );

        report('register', { message: 'Registering functions…' });
        await dispatch(listReportFunctions());
        let registered = getState().reportFunctionList?.items ?? [];

        const findRegistered = (fnName, dsId) => registered.find(f => {
            const name = f.functionName ?? f.FunctionName;
            const schema = f.schema ?? f.Schema ?? 'public';
            const sourceId = f.dataSourceId ?? f.DataSourceId;
            return name === fnName && schema === 'public' && sourceId === dsId;
        });

        let registeredCount = 0;
        for (const tpl of templatesToDeploy) {
            const fnName = tpl.defaultFunctionName ?? tpl.key;
            const dsId = dataSourceIdForTemplate(tpl, tenantDataSourceId);
            if (findRegistered(fnName, dsId)) {
                registeredCount += 1;
                report('register', { current: registeredCount, total, message: `Register ${fnName} (exists)` });
                continue;
            }
            const created = await dispatch(createReportFunction({
                dataSourceId: dsId,
                schema: 'public',
                functionName: fnName,
                displayName: tpl.label ?? fnName.replace(/_/g, ' '),
                description: tpl.description ?? null,
                enabled: true,
            }));
            if (created && !created.error) {
                registered.push(created);
            } else {
                deployErrors.push(`${fnName}: register failed`);
            }
            registeredCount += 1;
            report('register', { current: registeredCount, total, message: `Register ${fnName}…` });
        }

        await dispatch(listReportFunctions());
        registered = getState().reportFunctionList?.items ?? [];

        report('dashboard', { message: 'Creating dashboards…' });
        const { dashboards: dashboardDefs, warnings, missing } = buildDefaultDashboards(
            registered,
            DEFAULT_DASHBOARD_SECTIONS,
            dataSourceIds,
        );

        if (dashboardDefs.length === 0) {
            return {
                error: missing.length > 0
                    ? `Missing registered functions: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`
                    : 'No dashboards could be built from defaults',
                warnings,
                deployErrors,
            };
        }

        const existing = getState().reportDashboardList?.items ?? [];
        let displayOrder = existing.length === 0
            ? 0
            : Math.max(...existing.map(d => d.displayOrder ?? 0)) + 1;

        const createdDashboards = [];
        const totalDash = dashboardDefs.length;
        let dashIndex = 0;

        for (const def of dashboardDefs) {
            dashIndex += 1;
            report('dashboard', {
                current: dashIndex,
                total: totalDash,
                message: `Creating ${def.name}…`,
            });
            const created = await dispatch(createReportDashboard({
                applicationUserId,
                name: def.name,
                description: def.description,
                layoutJson: def.layoutJson,
                isActive: true,
                displayOrder: displayOrder++,
            }));
            if (created) createdDashboards.push(created);
        }

        if (createdDashboards.length === 0) {
            return { error: 'Failed to create dashboards', warnings, deployErrors };
        }

        await dispatch(listReportDashboardsByUser(applicationUserId));

        return {
            dashboards: createdDashboards,
            dashboard: createdDashboards[0],
            warnings,
            deployErrors: deployErrors.length > 0 ? deployErrors : undefined,
        };
    } catch (e) {
        return { error: e.response?.data?.detail ?? e.message };
    }
};
